import { useCallback, useEffect, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { RepSchemeType } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Container, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPen,
  faPlus,
  faSave,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import {
  ConfigProvider,
  Empty,
  Form,
  GetProp,
  Space,
  Table,
  Input,
} from "antd";
import IconButton from "../../common/IconButton";
import { PagedResults } from "../../types/generics";
import ListPageHeader from "../../common/ListPageHeader";
import ListPanel from "../../common/ListPanel";

export default function RepSchemeTypesList() {
  const { showToast } = useToasts();
  const [repSchemeTypes, setRepSchemeTypes] = useState<RepSchemeType[]>();
  const [error, setError] = useState(false);
  const [totalRepSchemeTypes, setTotalRepSchemeTypes] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
  }>({
    pageSize: 20,
    currentPage: 1,
  });

  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [form] = Form.useForm();
  const [selectedItem, setSelectedItem] = useState<RepSchemeType>();

  const initRepSchemeTypes = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);

    Entities.getPaginatedRepSchemeTypes(urlParams).then(
      ({ data }) => {
        setRepSchemeTypes((data as PagedResults<RepSchemeType>).items);
        setTotalRepSchemeTypes(
          (data as PagedResults<RepSchemeType>).totalItems
        );
      },
      () => {
        showToast(
          "repstype-error",
          "Impossibile ottenere la lista dei Rep Scheme Type",
          "",
          "danger"
        );
        setRepSchemeTypes([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const reset = useCallback(() => {
    setError(false);
    setRepSchemeTypes(undefined);
    initRepSchemeTypes();
  }, [initRepSchemeTypes]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  }, []);

  const handleEditClick = useCallback(
    (item?: RepSchemeType) => () => {
      setSelectedItem(item);
      setAddModal(true);
    },
    []
  );

  const handleDeleteClick = useCallback(
    (item: RepSchemeType) => () => {
      setSelectedItem(item);
      setDeleteModal(true);
    },
    []
  );

  const renderToolbar = useCallback(() => {
    return (
      <ListPageHeader
        title="Rep Scheme Type"
        count={totalRepSchemeTypes}
        actions={
          <button onClick={handleEditClick()} className="button-primary">
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">
              Aggiungi Rep Scheme Type
            </span>
          </button>
        }
      />
    );
  }, [handleEditClick, totalRepSchemeTypes]);

  const renderEmpty: GetProp<typeof ConfigProvider, "renderEmpty"> = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="Nessun Rep Scheme Type"
    />
  );

  const renderTable = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi.
          </span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        bordered
        dataSource={repSchemeTypes}
        rowKey="id"
        locale={{ emptyText: renderEmpty() }}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
          total: totalRepSchemeTypes,
        }}
      >
        <Table.Column
          key="repSchemeType"
          dataIndex="repSchemeType"
          title="Tipo di Rep Scheme"
          className="text-center col-pattern"
        />
        <Table.Column
          key="actions"
          title="Azioni"
          className="text-center col-pattern"
          render={(_value, record: RepSchemeType) => (
            <Space size={1}>
              <IconButton tooltip="Modifica" onClick={handleEditClick(record)}>
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
              <IconButton
                tooltip="Elimina"
                className="text-danger"
                onClick={handleDeleteClick(record)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    );
  }, [
    repSchemeTypes,
    error,
    filters,
    totalRepSchemeTypes,
    handlePageChange,
    handlePageSizeChange,
    handleEditClick,
    handleDeleteClick,
  ]);

  const renderAsyncContent = useCallback(() => {
    return <ListPanel loading={!repSchemeTypes}>{renderTable()}</ListPanel>;
  }, [repSchemeTypes, renderTable]);

  const handleAddModalClose = useCallback(() => {
    setAddModal(false);
    form.resetFields();
    setSelectedItem(undefined);
  }, [form]);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModal(false);
    setSelectedItem(undefined);
  }, []);

  const handleSubmit = useCallback(
    (values: any) => {
      setLoading(true);
      const body = { ...values };
      const promise = selectedItem
        ? Entities.updateRepSchemeType(selectedItem.id!, body)
        : Entities.createRepSchemeType(body);

      promise.then(
        () => {
          setLoading(false);
          showToast(
            "repstype-success",
            `Rep Scheme Type ${
              selectedItem ? "modificato" : "creato"
            } correttamente`,
            undefined,
            "success"
          );
          handleAddModalClose();
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            "repstype-error",
            `Errore nella ${
              selectedItem ? "modifica" : "creazione"
            } del Rep Scheme Type`,
            undefined,
            "danger"
          );
        }
      );
    },
    [selectedItem, showToast, handleAddModalClose, reset]
  );

  const handleDelete = useCallback(() => {
    setLoading(true);
    Entities.deleteRepSchemeType(selectedItem!.id!).then(
      () => {
        setLoading(false);
        handleDeleteModalClose();
        reset();
        showToast(
          "delete-repstype-success",
          "Rep Scheme Type eliminato correttamente",
          undefined,
          "success"
        );
      },
      () => {
        setLoading(false);
        showToast(
          "delete-repstype-error",
          "Errore nella cancellazione, per favore riprova",
          undefined,
          "danger"
        );
      }
    );
  }, [selectedItem, showToast, handleDeleteModalClose, reset]);

  return (
    <Container fluid="md" className="mt-5 list-page-container">
      {renderToolbar()}
      {renderAsyncContent()}

      {/* MODALE AGGIUNTA / MODIFICA */}
      <Modal show={addModal} onHide={handleAddModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedItem ? "Modifica" : "Nuovo"} Rep Scheme Type
          </Modal.Title>
        </Modal.Header>
        <Form form={form} onFinish={handleSubmit}>
          <Modal.Body>
            <Form.Item
              label="Nome del Tipo di Rep Scheme"
              name="repSchemeType"
              rules={[{ required: true, message: "Campo obbligatorio" }]}
            >
              <Input className="w-100" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button
              onClick={handleAddModalClose}
              variant="light"
              size="sm"
              className="border me-2"
            >
              Annulla
            </Button>
            <Button variant="success" size="sm" type="submit">
              {loading ? (
                <TailSpin width={15} height={15} color="#9c1419" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  <span className="ms-2">Salva</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE ELIMINAZIONE */}
      <Modal show={deleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina Rep Scheme Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare il Rep Scheme Type{" "}
            <strong>{selectedItem?.repSchemeType}</strong>?
          </p>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteModalClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={handleDelete}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
